/**
 * PreflightCard — read-only pre-flight inspection display card.
 *
 * Spec: LAYER_LIFECYCLE_AND_PORTABLE_LAYER_AUDIT.md §10.3
 * Phase: LAYER-LIFECYCLE-ALIGNMENT Phase E
 *
 * Shows a summary of what was dropped and what will happen before any
 * commit action is taken. Token-driven styling only.
 *
 * Accessibility:
 *   • role="region" + aria-labelledby on the card container.
 *   • All interactive elements are ≥ 40px touch targets.
 *   • Budget status communicated via both color token AND icon shape.
 *   • Warnings list uses role="list" with explicit aria-label.
 */

import { useId, type ReactNode } from 'react';
import type { PreflightInspection } from './preflightTypes';
import { tokens } from '@/design';

// ─── Artifact kind icon mapping ──────────────────────────────────────────────
// Maps ArtifactKind to a Material Symbols codepoint name.
// Data-driven — no domain branching in render code.

const ARTIFACT_ICON_MAP: Record<string, string> = {
  cog: 'satellite_alt',
  tileset3d: 'view_in_ar',
  glb: 'architecture',
  'ifc-glb': 'architecture',
  copc: 'scatter_plot',
  potree: 'scatter_plot',
  pmtiles: 'layers',
  geojson: 'map',
  flatgeobuf: 'map',
  geoparquet: 'table_rows',
  dxf: 'schema',
  shp: 'layers',
  e57: 'scatter_plot',
  las: 'scatter_plot',
  laz: 'scatter_plot',
  glayer: 'deployed_code',
  unknown: 'help_outline',
} as const;

// ─── Budget status → tokens ──────────────────────────────────────────────────

const BUDGET_STATUS_TOKEN: Record<string, { colorToken: string; icon: string; label: string }> = {
  ok: {
    colorToken: 'var(--color-primary)',
    icon: 'check_circle',
    label: 'Within budget',
  },
  warn: {
    colorToken: 'var(--color-tertiary)',
    icon: 'warning',
    label: 'Budget warning',
  },
  exceed: {
    colorToken: 'var(--color-error)',
    icon: 'error',
    label: 'Budget exceeded',
  },
} as const;

// ─── Align directive → human-readable label ───────────────────────────────────

const ALIGN_LABEL: Record<string, string> = {
  identity: 'No reprojection needed',
  reproject: 'Reprojection required',
  reject: 'Cannot align — CRS missing or incompatible',
} as const;

// ─── Recommended action → CTA label ──────────────────────────────────────────

const ACTION_LABEL: Record<string, string> = {
  pin: 'Pin',
  'reconcile-crs': 'Open CRS Wizard',
  'convert-first': 'Convert first (ad-hoc run)',
  reject: 'Cannot pin',
  'import-glayer': 'Import layer',
} as const;

// ─── Styles (token-driven) ────────────────────────────────────────────────────

const S = {
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--spacing-md)',
    padding: 'var(--spacing-lg)',
    backgroundColor: 'var(--color-surface-container)',
    borderRadius: 'var(--shape-corner-medium)',
    boxShadow: 'var(--elevation-level2)',
    fontFamily: 'var(--typography-font-family-base)',
    minWidth: 320,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
  },
  headerIcon: {
    fontFamily: 'Material Symbols Rounded',
    fontSize: 'var(--spacing-icon-size-md)',
    color: 'var(--color-on-surface-variant)',
    lineHeight: 1,
    flexShrink: 0,
  } as React.CSSProperties,
  headerTitle: {
    fontSize: 'var(--typography-title-sm-size)',
    fontWeight: 'var(--typography-title-sm-weight)' as React.CSSProperties['fontWeight'],
    lineHeight: 'var(--typography-title-sm-line-height)',
    color: 'var(--color-on-surface)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  headerSubtitle: {
    fontSize: 'var(--typography-label-sm-size)',
    color: 'var(--color-on-surface-variant)',
    lineHeight: 'var(--typography-label-sm-line-height)',
  },
  divider: {
    borderTop: `${tokens.border.width.thin}px solid var(--color-outline-variant)`,
    margin: 0,
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    minHeight: 'var(--spacing-touch-target)',
  },
  metaLabel: {
    fontSize: 'var(--typography-label-sm-size)',
    color: 'var(--color-on-surface-variant)',
    flexShrink: 0,
  },
  metaValue: {
    fontSize: 'var(--typography-label-md-size)',
    color: 'var(--color-on-surface)',
    textAlign: 'right' as const,
  },
  budgetRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
  },
  budgetIcon: {
    fontFamily: 'Material Symbols Rounded',
    fontSize: 'var(--spacing-icon-size-sm)',
    lineHeight: 1,
    flexShrink: 0,
  } as React.CSSProperties,
  warningsSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--spacing-xs)',
  },
  warningsHeading: {
    fontSize: 'var(--typography-label-sm-size)',
    fontWeight: 'var(--typography-label-sm-weight)' as React.CSSProperties['fontWeight'],
    color: 'var(--color-on-surface-variant)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
  },
  warningItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--spacing-xs)',
    fontSize: 'var(--typography-body-sm-size)',
    color: 'var(--color-on-surface)',
    lineHeight: 'var(--typography-body-sm-line-height)',
  },
  warningIcon: {
    fontFamily: 'Material Symbols Rounded',
    fontSize: 'var(--spacing-icon-size-sm)',
    color: 'var(--color-tertiary)',
    flexShrink: 0,
    marginTop: 1,
    lineHeight: 1,
  } as React.CSSProperties,
  actionRow: {
    display: 'flex',
    gap: 'var(--spacing-sm)',
    justifyContent: 'flex-end',
    flexWrap: 'wrap' as const,
    paddingTop: 'var(--spacing-sm)',
  },
  btnBase: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'var(--spacing-touch-target)',
    minWidth: 'var(--spacing-touch-target)',
    padding: '0 var(--spacing-md)',
    borderRadius: 'var(--shape-corner-full)',
    fontSize: 'var(--typography-label-md-size)',
    fontWeight: 'var(--typography-label-md-weight)' as React.CSSProperties['fontWeight'],
    fontFamily: 'var(--typography-font-family-base)',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    transition: 'background-color var(--motion-duration-short3) var(--motion-easing-standard)',
  },
  btnPrimary: {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
  },
  btnTonal: {
    backgroundColor: 'var(--color-secondary-container)',
    color: 'var(--color-on-secondary-container)',
  },
  btnText: {
    backgroundColor: 'transparent',
    color: 'var(--color-on-surface-variant)',
  },
  crsCodeChip: {
    display: 'inline-block',
    fontFamily: 'var(--typography-font-family-mono)',
    fontSize: 'var(--typography-body-sm-size)',
    backgroundColor: 'var(--color-surface-container-highest)',
    color: 'var(--color-on-surface)',
    borderRadius: 'var(--shape-corner-extra-small)',
    padding: '0 var(--spacing-xs)',
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={S.metaRow}>
      <span style={S.metaLabel}>{label}</span>
      <span style={S.metaValue}>{children}</span>
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PreflightCardProps {
  inspection: PreflightInspection;
  onPin: () => void;
  onCancel: () => void;
  onReconcileCrs: () => void;
  onConvert?: () => void;
  onImportGlayer?: () => void;
  busy?: boolean;
  busyLabel?: string;
  /** When set, surfaces a pipeline error inside the card (WP-D). */
  errorMessage?: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PreflightCard({
  inspection,
  onPin,
  onCancel,
  onReconcileCrs,
  onConvert,
  onImportGlayer,
  busy = false,
  busyLabel,
  errorMessage,
}: PreflightCardProps): ReactNode {
  const headingId = useId();
  const {
    fileName,
    fileSize,
    url,
    detectedKind,
    detectedSlot,
    detectedLayerKind,
    crs,
    budget,
    alignDirective,
    recommendedAction,
    warnings,
  } = inspection;

  const artifactIcon = ARTIFACT_ICON_MAP[detectedKind] ?? 'help_outline';
  const budgetTokens = BUDGET_STATUS_TOKEN[budget.status] ?? BUDGET_STATUS_TOKEN.ok;

  const displayName = fileName ?? url ?? 'Dropped artifact';
  const fileSizeLabel = fileSize !== undefined
    ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB`
    : undefined;

  return (
    <section
      role="region"
      aria-labelledby={headingId}
      style={S.card}
    >
      {/* ── Header ── */}
      <div style={S.header}>
        <span
          style={S.headerIcon}
          aria-hidden="true"
        >
          {artifactIcon}
        </span>
        <div style={{ overflow: 'hidden' }}>
          <div
            id={headingId}
            style={S.headerTitle}
            title={displayName}
          >
            {displayName}
          </div>
          {fileSizeLabel && (
            <div style={S.headerSubtitle}>
              {fileSizeLabel}
            </div>
          )}
        </div>
      </div>

      <hr style={S.divider} />

      {/* ── Metadata rows ── */}
      <MetaRow label="Detected kind">
        <span style={S.metaValue}>{detectedKind}</span>
      </MetaRow>

      <MetaRow label="Target slot">
        <span style={S.metaValue}>{detectedSlot}</span>
      </MetaRow>

      <MetaRow label="Layer kind">
        <span style={S.metaValue}>{detectedLayerKind}</span>
      </MetaRow>

      {crs && (
        <MetaRow label="CRS">
          <span style={S.crsCodeChip} title={`Coordinate mode: ${crs.coordinateMode}`}>
            {crs.code}
          </span>
        </MetaRow>
      )}

      <MetaRow label="Alignment">
        <span style={S.metaValue}>{ALIGN_LABEL[alignDirective] ?? alignDirective}</span>
      </MetaRow>

      {/* ── Budget gauge ── */}
      <MetaRow label="Budget">
        <div style={S.budgetRow}>
          {/* Non-color cue: icon shape carries budget state independently of color */}
          <span
            style={{
              ...S.budgetIcon,
              color: budgetTokens.colorToken,
            }}
            aria-label={budgetTokens.label}
          >
            {budgetTokens.icon}
          </span>
          <span
            style={{
              ...S.metaValue,
              color: budgetTokens.colorToken,
            }}
          >
            {budget.details}
          </span>
        </div>
      </MetaRow>

      {/* ── Warnings ── */}
      {warnings.length > 0 && (
        <div style={S.warningsSection}>
          <div style={S.warningsHeading}>
            <span
              style={{ fontFamily: 'Material Symbols Rounded', fontSize: 'var(--spacing-icon-size-sm)', color: 'var(--color-tertiary)', lineHeight: 1 }}
              aria-hidden="true"
            >
              info
            </span>
            Notices
          </div>
          <ul
            role="list"
            aria-label="Pre-flight warnings"
            style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}
          >
            {warnings.map((warning, index) => (
              // Warning items are generated from a static array; the index is
              // stable for the lifetime of this inspection object.
               
              <li key={index} style={S.warningItem}>
                <span
                  style={S.warningIcon}
                  aria-hidden="true"
                >
                  warning
                </span>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <hr style={S.divider} />

      {errorMessage ? (
        <div
          role="alert"
          data-testid="preflight-error"
          style={{
            padding: 'var(--spacing-sm)',
            borderRadius: 'var(--shape-corner-small)',
            backgroundColor: 'var(--color-error-container)',
            color: 'var(--color-on-error-container)',
            fontSize: 'var(--typography-body-sm-size)',
            lineHeight: 'var(--typography-body-sm-line-height)',
          }}
        >
          {errorMessage}
        </div>
      ) : null}

      {/* ── Action row ── */}
      <div style={S.actionRow}>
        <button
          type="button"
          style={{ ...S.btnBase, ...S.btnText }}
          onClick={onCancel}
          disabled={busy}
          aria-label="Cancel pre-flight inspection"
        >
          Cancel
        </button>

        {recommendedAction === 'reconcile-crs' && (
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnTonal }}
            onClick={onReconcileCrs}
            disabled={busy}
            aria-label="Open CRS reconciliation wizard"
          >
            {ACTION_LABEL['reconcile-crs']}
          </button>
        )}

        {recommendedAction === 'pin' && (
          /* Primary CTA — only one per action row per DESIGN_POLICY.md §5 */
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnPrimary }}
            onClick={onPin}
            disabled={busy}
            aria-label={`Pin ${displayName} to scene`}
          >
            {busy ? (busyLabel ?? 'Pinning…') : ACTION_LABEL.pin}
          </button>
        )}

        {recommendedAction === 'convert-first' && (
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnPrimary }}
            onClick={onConvert}
            disabled={busy || !onConvert}
            aria-disabled={busy || !onConvert}
            aria-label={ACTION_LABEL['convert-first']}
          >
            {busy ? (busyLabel ?? 'Converting…') : ACTION_LABEL['convert-first']}
          </button>
        )}

        {recommendedAction === 'import-glayer' && (
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnPrimary }}
            onClick={onImportGlayer}
            disabled={busy || !onImportGlayer}
            aria-disabled={busy || !onImportGlayer}
            aria-label={ACTION_LABEL['import-glayer']}
          >
            {busy ? (busyLabel ?? 'Importing…') : ACTION_LABEL['import-glayer']}
          </button>
        )}

        {recommendedAction === 'reject' && (
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnText }}
            disabled
            aria-disabled="true"
            aria-label={ACTION_LABEL.reject}
          >
            {ACTION_LABEL.reject}
          </button>
        )}
      </div>
    </section>
  );
}
